import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Zap, Plus, Trash2, X } from "lucide-react";

export function QuickReplies({ onPick }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const load = () => api.get("/quick-replies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/quick-replies", form); setForm({ title: "", body: "" }); setAdding(false); load(); toast.success("Quick reply saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { await api.delete(`/quick-replies/${id}`); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-10 w-10 p-0 shrink-0" title="Quick replies" data-testid="quick-replies-btn"><Zap className="w-4 h-4 text-[#F83A84]" /></Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2" data-testid="quick-replies-panel">
        <div className="flex items-center justify-between px-1 mb-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-[#6B7189]">Quick replies</span>
          <button onClick={() => setAdding(!adding)} className="text-[#0B7C5A]" data-testid="quick-reply-add-toggle">{adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</button>
        </div>
        {adding && (
          <div className="space-y-2 p-2 bg-[#F5F6FA] rounded-lg mb-2">
            <Input data-testid="quick-reply-title" placeholder="Title (e.g. Quote soon)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-9" />
            <Textarea data-testid="quick-reply-body" rows={2} placeholder="Message text…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <Button size="sm" onClick={save} disabled={!form.title.trim() || !form.body.trim()} className="h-8 bg-[#101D51] w-full" data-testid="quick-reply-save">Save</Button>
          </div>
        )}
        <div className="max-h-64 overflow-y-auto divide-y divide-[#F0F1F5]">
          {items.length === 0 && !adding && <div className="text-xs text-[#6B7189] p-3 text-center">No quick replies yet. Tap + to add one.</div>}
          {items.map((r) => (
            <div key={r.id} className="flex items-start gap-2 py-1.5 px-1 group">
              <button onClick={() => { onPick(r.body); setOpen(false); }} className="flex-1 text-left" data-testid={`quick-reply-${r.id}`}>
                <div className="text-sm font-medium text-[#101D51]">{r.title}</div>
                <div className="text-xs text-[#6B7189] line-clamp-2">{r.body}</div>
              </button>
              <button onClick={() => del(r.id)} className="text-[#6B7189] hover:text-[#F83A84] mt-1" data-testid={`quick-reply-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
